import React, { useMemo } from "react";

import { Instant } from "@js-joda/core";
import useDashboardStore from "store/dashboard-store";
import { DweenTimeUnitEnum, toInstant, toInstantOrNow } from "utils/time-utils";
import useTimeState from "hooks/dashboard/useTimeState";
import { useGetDataInfoRangeWithLoadConf, useGetRecentData } from "api/api-hooks/datasets-hooks";
import { useCompanyTimezone } from "hooks/useTimezone";
import { isNumeric } from "utils/utils";
import SparkLineHandler from "./SparkLineHandler";
import { AdvancedProps, Datapoint, SparkLineProps, Usages } from "./SparkLineTypes";
import { defaultFromDateTimeInstant, defaultToDateTimeInstant } from "../charts/utils";
import { defaultLoadConf } from "../data-input-output-table/defaultConfigs";

export default function SparkLine(props: SparkLineProps) {
  const count = props.count || 14;
  const isEditMode = useDashboardStore(state => state.isEditMode);

  const companyTimezone = useCompanyTimezone();

  const dataset = isNumeric(props.dataset)
    ? `${props.dataset}:CATEGORIZED_DATASET`
    : (props.dataset as string);

  const timeState = useTimeState({
    from: props.from,
    to: props.to,
  });

  const defaultFromDate =
    toInstant(timeState.from) ??
    defaultFromDateTimeInstant({
      zoneId: companyTimezone,
      loadConf: {
        alignUnit: DweenTimeUnitEnum.MINUTES,
        lag: 6,
        lagUnit: DweenTimeUnitEnum.HOURS,
        lead: 0,
        leadUnit: DweenTimeUnitEnum.HOURS,
        ...props.loadConf,
      },
      referenceTime: toInstantOrNow(props.referenceTime),
    });

  const defaultToDate =
    toInstant(timeState.to) ??
    defaultToDateTimeInstant({
      zoneId: companyTimezone,
      loadConf: {
        alignUnit: DweenTimeUnitEnum.MINUTES,
        lag: 6,
        lagUnit: DweenTimeUnitEnum.HOURS,
        lead: 0,
        leadUnit: DweenTimeUnitEnum.HOURS,
        ...props.loadConf,
      },
      referenceTime: toInstantOrNow(props.referenceTime),
    });

  const refetchInterval = props.updateFrequency && !isEditMode ? props.updateFrequency * 1000 : 0;

  const { data, isLoading } = useGetRecentData([dataset], {
    count,
    refTime: props.referenceTime,
    options: {
      refetchInterval,
      enabled: !!props.dataset && !props.useLoadConf,
    },
  });

  const { data: dataRange, isLoading: isDataRangeLoading } = useGetDataInfoRangeWithLoadConf(
    [dataset],
    {
      from: toInstant(timeState.from),
      to: toInstant(timeState.to),
      loadConf: props.loadConf ?? defaultLoadConf,
      downsampleInterval: props.downsampleInterval,
      useDefaultDownsampleInterval: true,
      referenceTime: props.referenceTime,
    },
    {
      refetchInterval,
      enabled: !!props.dataset && props.useLoadConf,
    }
  );

  const sortedData = useMemo(() => {
    if (props.useLoadConf) {
      return dataRange?.[0].data.sort((a, b) => a.key.localeCompare(b.key)) || [];
    }
    return data?.[0].data.sort((a, b) => a.key.localeCompare(b.key)) || [];
  }, [data, dataRange, props.useLoadConf]);

  const transformedData = useMemo((): {
    dataArray: number[];
    lastValueTime: null | Instant;
    prevValueTime: null | Instant;
    unit?: string;
    decimalPlaces?: number;
    title?: string;
  } => {
    if (!props.useLoadConf && !data?.[0]?.data?.length)
      return { dataArray: [], lastValueTime: null, prevValueTime: null };
    if (props.useLoadConf && !dataRange?.[0]?.data?.length)
      return { dataArray: [], lastValueTime: null, prevValueTime: null };

    const values = sortedData.map(d => Number(d.value));
    const lastValueTime = Instant.parse(sortedData[sortedData.length - 1].key);
    const prevValueTime = sortedData.length > 1 ? Instant.parse(sortedData[sortedData.length - 2].key) : null;

    const unit = data?.[0]?.unit ?? dataRange?.[0]?.unit ?? "";
    const decimalPlaces =
      props.decimalPlaces ?? data?.[0]?.decimalPlaces ?? dataRange?.[0]?.decimalPlaces ?? 0;
    const title = props.useLoadConf ? data?.[0]?.title : dataRange?.[0]?.title;

    return { dataArray: values, lastValueTime, prevValueTime, unit, decimalPlaces, title };
  }, [data, dataRange, sortedData, props.useLoadConf, props.decimalPlaces]);

  const formattedData: Datapoint[] = useMemo(() => {
    return sortedData.reduce<Datapoint[]>((acc, d) => {
      if (d.value != null) {
        acc.push({ y: d.value, x: Instant.parse(d.key).toEpochMilli() });
      }
      return acc;
    }, []);
  }, [sortedData]);

  // props from legacy config
  const legacyProps = {
    // @ts-expect-error
    constant: props.constant,
    // @ts-expect-error
    dataset: props.baselineDataset,
    // @ts-expect-error
    usage: (props.useConstant ? "constant" : "dataset") as Usages,
    // @ts-expect-error
    color: props.baselineColor,
    // @ts-expect-error
    width: props.baselineWidth,
    // @ts-expect-error
    fillAboveColor: props.fillAboveColor,
    // @ts-expect-error
    fillBelowColor: props.fillBelowColor,
  };

  const getFromDate = (): Instant => {
    if (props.useLoadConf) {
      return defaultFromDate;
    }
    const fromDate = data?.[0].data[0].key;
    return fromDate ? Instant.parse(fromDate) : Instant.now();
  };

  const getToDate = (): Instant => {
    if (props.useLoadConf) {
      return defaultToDate;
    }
    const dataArray = data?.[0].data;
    const toDate = dataArray?.[dataArray.length - 1]?.key;

    return toDate ? Instant.parse(toDate) : Instant.now();
  };

  const isMultipleChartsInOneBox =
    props.multipleCharts?.length > 0 && props.isMultipleCharts && props.generalProperties?.chartsInOneBox;

  const advancedProps: AdvancedProps = {
    baseline: {
      ...legacyProps,
      ...props.baseline,
    },
    data: formattedData,
    minLine: props.minLine,
    maxLine: props.maxLine,
    count,
    updateFrequency: props.updateFrequency,
    referenceTime: props.referenceTime,
    showYAxis: props.showYAxis,
    fromDate: getFromDate(),
    toDate: getToDate(),
    isMultipleCharts: props.isMultipleCharts,
    isExpanded: props.isExpanded,
    setExpandedChartIndex: props.setExpandedChartIndex,
    multipleCharts: isMultipleChartsInOneBox ? props.multipleCharts : [],
    allCharts: props.allCharts,
    isMultipleChartsInOneBox,
    useDecimalsFromHundred: props.useDecimalsFromHundred,
    downsampleInterval: props.downsampleInterval,
    showValueChangeIndicator: props.showValueChangeIndicator,
    useDiffIndicatorColors: props.useDiffIndicatorColors,
    diffNumFontSize: props.diffNumFontSize,
    diffArrowFontSize: props.diffArrowFontSize,
  };

  return (
    <SparkLineHandler
      isLoading={isLoading || isDataRangeLoading}
      dataArray={transformedData.dataArray}
      chartIndex={props.chartIndex}
      lastValueTime={transformedData.lastValueTime}
      prevValueTime={transformedData.prevValueTime}
      decimalPlaces={transformedData.decimalPlaces}
      labelFontSize={props.labelFontSize}
      sparklineWidth={props.sparkLineWidth}
      lineColor={props.lineColor}
      lineWidth={props.lineWidth}
      chartTitle={props.chartTitle}
      unit={transformedData.unit}
      maxAge={props.maxAge}
      dataset={dataset}
      title={transformedData.title ?? ""}
      height={props.height}
      advanced={props.advanced}
      advancedProps={advancedProps}
      generalProperties={props.generalProperties}
      isMultipleCharts={props.isMultipleCharts}
      stringValueMapper={data?.[0].stringValueMapper ?? dataRange?.[0].stringValueMapper}
      minYAxis={props.minYAxis}
      maxYAxis={props.maxYAxis}
    />
  );
}
